import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import T from 'i18n-react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TreeView from '@material-ui/lab/TreeView';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import TreeItem from '@material-ui/lab/TreeItem';
import Checkbox from '@material-ui/core/Checkbox';
import update from 'immutability-helper';
import arrayToTree from 'array-to-tree';
import FolderIcon from '@material-ui/icons/Folder';
import SvgIcon from '@material-ui/core/SvgIcon';
import _ from 'lodash';
import { generateUUID } from 'util/genrateUuid';
import { DEFAULT_LIST } from '../states';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  saveBtn: {
    background: '#003DA5',
    borderRadius: '2px',
    color: '#fff',
    '&:disabled': {
      background: '#c9c9c9',
    },
    '&:hover': {
      background: '#003DA5',
    },
  },
  createButton: {
    fontSize: '16px',
    padding: '10px 21.5px',
    color: '#fff',
    backgroundColor: '#2154A2',
  },
  cancelButton: {
    padding: '10px 21.5px',
  },
  checkbox: {
    padding: '0 !important',
  },
  checked: {
    color: '#0070E0 !important',
  },
  svgIcon: {
    margin: '0 0 0 10px',
    color: '#C6C6C6',
  },
}));

const FileSystemNavigator = (props) => {
  const classes = useStyles();

  const {
    open,
    setOpen,
    action,
    files,
    // filesInit,
    selected,
    setSelected,
    setShareFiles,
    handleBulkDownload,
  } = props;

  const filesTree = Object.keys(files).filter((key) => key !== 'default');

  const handleClose = () => {
    setOpen(false);
    // const resetSelected = Object.keys(selected).reduce((acc, current) => {
    //   acc[current] = [];
    //   return acc;
    // }, {});
    // setSelected(resetSelected);
  };

  const treeToArray = (files) => {
    let fileArr;

    if (files.children) {
      fileArr = files.children.map((file) => {
        if (file.children) {
          return subTreeToArray(file.children);
        }
        return file;
      });
    }

    return fileArr;
  };

  const subTreeToArray = (files) => {
    if (files) {
      return files.map((file) => {
        if (file.children) {
          return subTreeToArray(file.children);
        }
        return file;
      });
    }
  };

  const handleSubTreeChecked = (tree, key) => {
    if (tree.children) {
      return tree.children.map((file) => {
        if (file.children) {
          return handleSubTreeChecked(file.children, key);
        }

        const checked = !!selected[key].find((select) => select?.id === file?.id);
        return checked;
      });
    }

    if (Array.isArray(tree)) {
      return tree.map((file) => {
        if (file.children) {
          return handleSubTreeChecked(file.children, key);
        }

        const checked = !!selected[key].find((select) => select?.id === file?.id);
        return checked;
      });
    }

    const checked = Array.isArray(tree)
      ? tree.map((file) => {
          return !!selected[key].find((select) => select?.id === file?.id);
        })
      : tree;

    return checked;
  };

  const handleKeyCheck = (key) => {
    if (selected?.[key]?.length === 0) {
      setSelected((prevContent) => ({
        ...(prevContent || []),
        [key]: update(selected[key], {
          $push: files[key].filter((file) => file.url),
        }),
      }));
    } else {
      setSelected((prevContent) => ({
        ...(prevContent || []),
        [key]: update(selected[key], {
          $set: [],
        }),
      }));
    }
  };

  const handleCheck = (key, data) => {
    if (data.url) {
      const isSelected =
        selected?.[key] && (selected?.[key] ?? []).map((file) => file.id).indexOf(data.id);
      if (isSelected > -1) {
        const index = selected[key].findIndex((file) => file.id === data.id);
        if (selected[key].length === 1) {
          setSelected((prevContent) => ({
            ...(prevContent || []),
            [key]: update(selected[key], {
              $set: [],
            }),
          }));
        }
        setSelected((prevContent) => ({
          ...prevContent,
          [key]: update(selected[key], {
            $splice: [[index, 1]],
          }),
        }));
      } else {
        setSelected((prevContent) => ({
          ...prevContent,
          [key]: update(selected[key], {
            $push: [data],
          }),
        }));
      }
    } else {
      const subFiles = data.children ? _.flattenDeep(treeToArray(data)) : [];
      const checked = (selected[key] || []).map((select) => {
        if (select.parentId === data.id) {
          return true;
        }
        if (select.id === data.id) {
          return true;
        }
        return false;
      });

      const fileArr = selected[key].filter((file) => {
        return !subFiles.some((subFile) => {
          return file.id === subFile.id;
        });
      });

      if (checked.indexOf(true) > -1) {
        setSelected((prevContent) => ({
          ...prevContent,
          [key]: update(selected[key], {
            $set: fileArr,
          }),
        }));
      } else {
        setSelected((prevContent) => ({
          ...prevContent,
          [key]: update(selected[key], {
            $push: [...subFiles],
          }),
        }));
      }
    }
  };

  const renderSubTree = (subTreeGroup, treeId, key) => {
    if (Array.isArray(subTreeGroup)) {
      return subTreeGroup.map((tree, idx) => {
        const nodeId = `node-tree-${generateUUID()}`;
        if (tree.url) {
          const isSelected = !!selected?.[key]?.find((select) => select.id === tree.id);
          return (
            <TreeItem
              nodeId={nodeId + idx}
              label={
                <Box display='flex' alignItems='flex-start' m={2}>
                  <Checkbox
                    checked={isSelected}
                    classes={{
                      root: classes.checkbox,
                      checked: classes.checked,
                    }}
                    size='small'
                    onChange={() => handleCheck(key, tree)}
                  />
                  <Box
                    fontSize='14px'
                    fontWeight='normal'
                    color={!isSelected ? 'rgba(0, 0, 0, 0.53)' : '#0070E0'}
                    mx={2}>
                    {tree.name}
                  </Box>
                </Box>
              }
              key={tree.id}
            />
          );
        }

        let checked =
          selected[key]?.length > 0
            ? !!selected[key].find((select) => select?.url && select?.parentId === tree?.id) ||
              _.flattenDeep(handleSubTreeChecked(tree, key))
            : false;

        if (checked.length > 0) {
          checked = checked.indexOf(true) > -1;
        }

        if (Array.isArray(checked) && (checked.length === 0 || checked.indexOf(true) === -1)) {
          checked = false;
        }

        return (
          <TreeItem
            nodeId={nodeId + idx}
            label={
              <Box display='flex' alignItems='flex-start' m={2}>
                <Checkbox
                  checked={checked}
                  classes={{ root: classes.checkbox, checked: classes.checked }}
                  size='small'
                  onChange={() => handleCheck(key, tree)}
                />
                <SvgIcon classes={{ root: classes.svgIcon }}>
                  <FolderIcon />
                </SvgIcon>
                {checked.length > 0 ? (
                  <Box
                    fontSize='14px'
                    fontWeight='500'
                    mx={1}
                    color={checked.indexOf(true) > -1 ? '#0070E0' : '#000000'}>
                    {tree.name}
                  </Box>
                ) : (
                  <Box
                    fontSize='14px'
                    fontWeight='500'
                    mx={1}
                    color={!checked ? '#000000' : '#0070E0'}>
                    {tree.name}
                  </Box>
                )}
              </Box>
            }
            key={tree.id}>
            {renderSubTree(tree.children, nodeId, key)}
          </TreeItem>
        );
      });
    }
  };

  const renderTreeView = (key) => {
    const treeGroup = arrayToTree(files[key], { parentProperty: 'parentId', customID: 'id' });
    return treeGroup
      .filter((item) => item.expiryDate || item.name || item.owner || item.parentId)
      .map((tree, idx) => {
        const nodeId = `node-tree-${generateUUID()}`;
        if (tree.url) {
          const isSelected = !!selected[key]?.find((select) => select?.id === tree?.id);
          return (
            <TreeItem
              nodeId={nodeId + idx}
              label={
                <Box display='flex' alignItems='flex-start' m={2}>
                  <Checkbox
                    checked={isSelected}
                    classes={{ root: classes.checkbox, checked: classes.checked }}
                    size='small'
                    onChange={() => handleCheck(key, tree)}
                  />
                  <Box
                    fontSize='14px'
                    fontWeight='normal'
                    color={!isSelected ? 'rgba(0, 0, 0, 0.53)' : '#0070E0'}
                    mx={2}>
                    {tree.name}
                  </Box>
                </Box>
              }
              key={tree.id}
            />
          );
        }
        let checked =
          selected[key]?.length > 0
            ? !!selected[key]?.find((select) => select?.parentId === tree?.id) ||
              _.flattenDeep(handleSubTreeChecked(tree, key))
            : false;

        if (checked.length > 0) {
          checked = checked.indexOf(true) > -1;
        }

        if (Array.isArray(checked) && (checked.length === 0 || checked.indexOf(true) === -1)) {
          checked = false;
        }

        return (
          <TreeItem
            nodeId={nodeId + idx}
            label={
              <Box display='flex' alignItems='flex-start' m={2}>
                <Checkbox
                  checked={checked}
                  classes={{ root: classes.checkbox, checked: classes.checked }}
                  size='small'
                  onChange={() => handleCheck(key, tree)}
                />
                <SvgIcon classes={{ root: classes.svgIcon }}>
                  <FolderIcon />
                </SvgIcon>
                {checked.length > 0 ? (
                  <Box
                    fontSize='14px'
                    fontWeight='500'
                    mx={1}
                    color={checked.indexOf(true) > -1 ? '#0070E0' : '#000000'}>
                    {tree.name}
                  </Box>
                ) : (
                  <Box
                    fontSize='14px'
                    fontWeight='500'
                    mx={1}
                    color={!checked ? '#000000' : '#0070E0'}>
                    {tree.name}
                  </Box>
                )}
              </Box>
            }
            key={tree.id}>
            {renderSubTree(tree.children, nodeId, key)}
          </TreeItem>
        );
      });
  };

  return (
    <div>
      <Dialog
        fullWidth
        maxWidth='md'
        open={open}
        onClose={handleClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'>
        <DialogTitle>
          <Grid container justifyContent='space-between'>
            <Box fontSize='16px' fontWeight='500' my={2}>
              {action === 'bulkDownload'
                ? T.translate('productManager.bulkDownload')
                : T.translate('productManager.shareFiles')}
            </Box>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <TreeView
              className={classes.root}
              defaultCollapseIcon={<ExpandMoreIcon />}
              defaultExpandIcon={<ChevronRightIcon />}>
              {filesTree.length > 0 &&
                filesTree.map((key, idx) => {
                  return (
                    <TreeItem
                      nodeId={`node-id-${idx}`}
                      label={
                        <Box display='flex' alignItems='flex-start' m={2}>
                          <Checkbox
                            checked={selected[key]?.length > 0}
                            classes={{
                              root: classes.checkbox,
                              checked: classes.checked,
                            }}
                            size='small'
                            onChange={() => handleKeyCheck(key)}
                          />
                          <Box
                            fontSize='14px'
                            fontWeight='500'
                            color={!selected[key]?.length > 0 ? '#000000' : '#0070E0'}
                            mx={2}>
                            {DEFAULT_LIST.includes(key)
                              ? T.translate(`productManager.tab.${key}`)
                              : key}
                          </Box>
                        </Box>
                      }
                      key={key}>
                      {renderTreeView(key, idx)}
                    </TreeItem>
                  );
                })}
            </TreeView>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant='text' classes={{ root: classes.cancelButton }}>
            <Box fontSize='14px' fontWeight='500' color='#003DA5'>
              {T.translate('general.cancel')}
            </Box>
          </Button>
          {action === 'bulkDownload' ? (
            <Button
              classes={{ root: classes.createButton }}
              onClick={() => {
                handleBulkDownload();
                handleClose();
              }}
              variant='contained'
              autoFocus>
              <Box fontSize='14px' fontWeight='500' color='#fff'>
                {T.translate('productManager.bulkDownload')}
              </Box>
            </Button>
          ) : (
            <Button
              classes={{ root: classes.createButton }}
              onClick={() => setShareFiles(true)}
              variant='contained'
              autoFocus>
              <Box fontSize='14px' fontWeight='500' color='#fff'>
                {T.translate('productManager.share')}
              </Box>
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

FileSystemNavigator.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  action: PropTypes.string,
  filesInit: PropTypes.object,
  selected: PropTypes.object,
  setSelected: PropTypes.func,
  setShareFiles: PropTypes.func,
  handleBulkDownload: PropTypes.func,
  files: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

export default FileSystemNavigator;
